import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { 
  Mail, 
  ArrowLeft, 
  Loader2, 
  Send, 
  CheckCircle, 
  AlertCircle,
  Eye,
  Settings,
  Gift,
  Trophy,
  User,
  Building2,
  Phone,
  MapPin,
  Globe,
  RefreshCw,
  Sparkles,
  Save,
  Clock,
  Link2,
  Calendar
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function OrganizerEmailSettings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState("participation");
  
  // Email config
  const [emailConfig, setEmailConfig] = useState({
    smtp_enabled: false,
    smtp_host: "",
    smtp_port: 465,
    smtp_user: "",
    smtp_password: "",
    use_ssl: true,
    from_email: "",
    from_name: "",
    last_test_status: null,
    last_test_at: null
  });
  
  // Organizer info for templates
  const [orgInfo, setOrgInfo] = useState({
    name: "",
    logo: "",
    email: "",
    phone: "",
    address: "",
    website: ""
  });
  
  // Test email address
  const [testEmail, setTestEmail] = useState("");

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const res = await api.get("/organizer/email-config");
      if (res.data.email_config) {
        setEmailConfig(prev => ({...prev, ...res.data.email_config}));
      }
      if (res.data.org_info) {
        setOrgInfo(prev => ({...prev, ...res.data.org_info}));
      }
      if (user?.email) {
        setTestEmail(user.email);
      }
    } catch (error) {
      console.error("Error loading config:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await api.put("/organizer/email-config", {
        email_config: emailConfig,
        org_info: orgInfo
      });
      toast.success("Configuration enregistrée");
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const res = await api.post("/organizer/email-config/test");
      setEmailConfig(prev => ({
        ...prev,
        last_test_status: "success",
        last_test_at: new Date().toISOString()
      }));
      toast.success("Connexion SMTP réussie !");
    } catch (error) {
      setEmailConfig(prev => ({
        ...prev,
        last_test_status: "failed",
        last_test_at: new Date().toISOString()
      }));
      toast.error(error.response?.data?.detail || "Erreur de connexion SMTP");
    } finally {
      setTesting(false);
    }
  };

  const handleSendTestEmail = async (templateType) => {
    if (!testEmail) {
      toast.error("Entrez une adresse email de test");
      return;
    }
    setSendingTest(true);
    try {
      await api.post("/organizer/email-config/send-test", {
        to_email: testEmail,
        template_type: templateType
      });
      toast.success(`Email de test "${templateType}" envoyé à ${testEmail}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSendingTest(false);
    }
  };

  // Demo data for preview
  const demoParticipant = {
    name: "Jean Dupont",
    reference: "T000042",
    custom_field_label: "N° de ticket",
    custom_field_value: "TCK-2025-042"
  };

  const demoPrize = {
    name: "iPhone 16 Pro",
    description: "Le dernier smartphone Apple avec puce A18",
    pickup_instructions: "Retrait en magasin sur présentation de ce code",
    code: "WINNER-2025-XYZ"
  };

  const demoDrawInfo = {
    title: "Grand Tirage de Noël 2025",
    pickup_deadline: "31 décembre 2025",
    results_url: "https://tireo.app/show/exemple-tirage",
    publication_deadline: "15 janvier 2026"
  };

  // Email templates preview
  const renderEmailPreview = () => {
    const logoUrl = orgInfo.logo || "https://via.placeholder.com/150x50?text=LOGO";
    const orgName = orgInfo.name || user?.company_name || "Votre Organisation";
    const contactInfo = orgInfo.phone || orgInfo.email || "contact@example.com";
    const address = orgInfo.address || "Adresse non renseignée";
    
    if (previewTemplate === "participation") {
      return (
        <div className="bg-white rounded-lg shadow-lg overflow-hidden max-w-md mx-auto">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-center">
            {orgInfo.logo && (
              <img src={orgInfo.logo} alt="" className="h-12 mx-auto mb-3 object-contain" />
            )}
            <h2 className="text-white text-xl font-bold">Merci pour votre participation !</h2>
          </div>
          
          {/* Content */}
          <div className="p-6 space-y-4">
            <p className="text-slate-700">
              Bonjour <strong>{demoParticipant.name}</strong>,
            </p>
            <p className="text-slate-600">
              Votre participation a bien été enregistrée. Voici vos informations :
            </p>
            
            <div className="bg-slate-50 rounded-lg p-4 border">
              <p className="text-sm text-slate-600">
                <strong>Référence :</strong> {demoParticipant.reference}
              </p>
              {demoParticipant.custom_field_label && (
                <p className="text-sm text-slate-600 mt-1">
                  <strong>{demoParticipant.custom_field_label} :</strong> {demoParticipant.custom_field_value}
                </p>
              )}
            </div>
            
            <p className="text-slate-600 text-sm">
              Conservez cette référence précieusement. Les résultats seront annoncés prochainement !
            </p>
            
            <p className="text-slate-600 text-sm">
              Bonne chance ! 🍀
            </p>
          </div>
          
          {/* Footer */}
          <div className="bg-slate-100 p-4 text-center text-sm text-slate-500">
            <p className="font-medium">{orgName}</p>
            <p>{contactInfo}</p>
            {orgInfo.address && <p>{address}</p>}
          </div>
        </div>
      );
    }
    
    if (previewTemplate === "winner") {
      return (
        <div className="bg-white rounded-lg shadow-lg overflow-hidden max-w-md mx-auto">
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500 to-yellow-500 p-6 text-center">
            {orgInfo.logo && (
              <img src={orgInfo.logo} alt="" className="h-12 mx-auto mb-3 object-contain" />
            )}
            <div className="flex items-center justify-center gap-2 mb-2">
              <Trophy className="w-8 h-8 text-white" />
              <h2 className="text-white text-2xl font-bold">FÉLICITATIONS !</h2>
              <Trophy className="w-8 h-8 text-white" />
            </div>
            <p className="text-white/90">Vous avez gagné !</p>
          </div>
          
          {/* Content */}
          <div className="p-6 space-y-4">
            <p className="text-slate-700">
              Bonjour <strong>{demoParticipant.name}</strong>,
            </p>
            <p className="text-slate-600">
              Nous avons le plaisir de vous annoncer que vous êtes l'heureux gagnant de notre tirage !
            </p>
            
            {/* Prize card */}
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg p-4 border border-amber-200">
              <div className="flex items-center gap-2 mb-2">
                <Gift className="w-5 h-5 text-amber-600" />
                <span className="font-bold text-amber-800">Votre lot</span>
              </div>
              <p className="text-lg font-bold text-slate-800">{demoPrize.name}</p>
              {demoPrize.description && (
                <p className="text-sm text-slate-600 mt-1">{demoPrize.description}</p>
              )}
            </div>
            
            {/* Pickup instructions */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <p className="font-medium text-blue-800 mb-2">📍 Comment récupérer votre lot :</p>
              {demoPrize.pickup_instructions ? (
                <p className="text-sm text-blue-700">{demoPrize.pickup_instructions}</p>
              ) : (
                <div className="text-sm text-blue-700 space-y-1">
                  <p>Contactez-nous pour récupérer votre lot :</p>
                  <p><strong>{orgName}</strong></p>
                  {orgInfo.phone && <p>📞 {orgInfo.phone}</p>}
                  {orgInfo.email && <p>✉️ {orgInfo.email}</p>}
                  {orgInfo.address && <p>📍 {orgInfo.address}</p>}
                </div>
              )}
            </div>
            
            {demoPrize.code && (
              <div className="text-center p-3 bg-slate-100 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Votre code gagnant</p>
                <p className="font-mono font-bold text-lg">{demoPrize.code}</p>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="bg-slate-100 p-4 text-center text-sm text-slate-500">
            <p className="font-medium">{orgName}</p>
            <p>{contactInfo}</p>
            {orgInfo.address && <p>{address}</p>}
          </div>
        </div>
      );
    }

    if (previewTemplate === "reminder") {
      return (
        <div className="bg-white rounded-lg shadow-lg overflow-hidden max-w-md mx-auto">
          {/* Header */}
          <div className="bg-gradient-to-r from-red-600 to-red-700 p-6 text-center">
            {orgInfo.logo && (
              <img src={orgInfo.logo} alt="" className="h-12 mx-auto mb-3 object-contain" />
            )}
            <div className="flex items-center justify-center gap-2 mb-2">
              <Clock className="w-6 h-6 text-white" />
              <h2 className="text-white text-xl font-bold">N'oubliez pas votre lot !</h2>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-6 space-y-4">
            <p className="text-slate-700">
              Bonjour <strong>{demoParticipant.name}</strong>,
            </p>
            <p className="text-slate-600">
              Nous vous rappelons que vous avez gagné lors de <strong>{demoDrawInfo.title}</strong> et que votre lot vous attend !
            </p>
            
            {/* Prize card */}
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg p-4 border-2 border-amber-400 text-center">
              <p className="text-sm text-amber-700 mb-1">🎁 Votre lot</p>
              <p className="text-xl font-bold text-slate-800">{demoPrize.name}</p>
            </div>
            
            {/* Deadline warning */}
            <div className="bg-red-50 rounded-lg p-4 border-2 border-red-200 text-center">
              <p className="font-bold text-red-800 mb-1">📅 Date limite de retrait</p>
              <p className="text-2xl font-bold text-red-600">{demoDrawInfo.pickup_deadline}</p>
              <p className="text-xs text-red-700 mt-1">Passé ce délai, le lot ne pourra plus être réclamé</p>
            </div>
            
            {/* Pickup instructions */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <p className="font-medium text-blue-800 mb-2">📍 Instructions de retrait :</p>
              <p className="text-sm text-blue-700">{demoPrize.pickup_instructions}</p>
            </div>
            
            {demoPrize.code && (
              <div className="text-center p-3 bg-slate-100 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Code gagnant</p>
                <p className="font-mono font-bold text-lg">{demoPrize.code}</p>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="bg-slate-100 p-4 text-center text-sm text-slate-500">
            <p className="font-medium">{orgName}</p>
            <p>{contactInfo}</p>
          </div>
        </div>
      );
    }

    if (previewTemplate === "results") {
      return (
        <div className="bg-white rounded-lg shadow-lg overflow-hidden max-w-md mx-auto">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-violet-600 p-6 text-center">
            {orgInfo.logo && (
              <img src={orgInfo.logo} alt="" className="h-12 mx-auto mb-3 object-contain" />
            )}
            <h2 className="text-white text-xl font-bold">🎲 Les résultats sont tombés !</h2>
          </div>
          
          {/* Content */}
          <div className="p-6 space-y-4">
            <p className="text-slate-700">
              Bonjour <strong>{demoParticipant.name}</strong>,
            </p>
            <p className="text-slate-600">
              Les résultats de <strong>{demoDrawInfo.title}</strong> sont maintenant disponibles !
            </p>
            
            {/* CTA Button */}
            <div className="text-center my-6">
              <p className="text-slate-600 text-sm mb-3">Découvrez si vous faites partie des heureux gagnants :</p>
              <div className="inline-block bg-gradient-to-r from-purple-600 to-violet-600 text-white font-bold py-3 px-8 rounded-full">
                🎯 Voir les résultats
              </div>
            </div>
            
            {/* Deadline info */}
            <div className="bg-purple-50 rounded-lg p-4 border border-purple-200 text-center">
              <p className="text-sm text-purple-700">📅 Les résultats resteront consultables jusqu'au</p>
              <p className="text-xl font-bold text-purple-600 mt-1">{demoDrawInfo.publication_deadline}</p>
            </div>
            
            <p className="text-center text-slate-500 text-sm">
              Merci pour votre participation et bonne chance pour les prochains tirages ! 🍀
            </p>
          </div>
          
          {/* Footer */}
          <div className="bg-slate-100 p-4 text-center text-sm text-slate-500">
            <p className="font-medium">{orgName}</p>
            <p>{contactInfo}</p>
          </div>
        </div>
      );
    }
    
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <div className="bg-card border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                Configuration des Emails
              </h1>
              <p className="text-sm text-muted-foreground">
                Paramètres SMTP et modèles d'emails
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <Tabs defaultValue="smtp" className="space-y-6">
          <TabsList>
            <TabsTrigger value="smtp" className="gap-2">
              <Settings className="w-4 h-4" />
              Configuration SMTP
            </TabsTrigger>
            <TabsTrigger value="templates" className="gap-2">
              <Eye className="w-4 h-4" />
              Aperçu des Emails
            </TabsTrigger>
            <TabsTrigger value="org" className="gap-2">
              <Building2 className="w-4 h-4" />
              Infos Organisateur
            </TabsTrigger>
          </TabsList>

          {/* SMTP Configuration */}
          <TabsContent value="smtp" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Mail className="w-5 h-5" />
                      Serveur SMTP
                    </CardTitle>
                    <CardDescription>
                      Configurez votre serveur d'envoi d'emails
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={emailConfig.smtp_enabled}
                      onCheckedChange={(checked) => setEmailConfig({...emailConfig, smtp_enabled: checked})}
                    />
                    <Label className="text-sm">{emailConfig.smtp_enabled ? "Activé" : "Désactivé"}</Label>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Status */}
                {emailConfig.last_test_at && (
                  <div className={`p-3 rounded-lg flex items-center gap-3 ${
                    emailConfig.last_test_status === "success" 
                      ? "bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800"
                      : "bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800"
                  }`}>
                    {emailConfig.last_test_status === "success" ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    )}
                    <div>
                      <p className={`font-medium ${emailConfig.last_test_status === "success" ? "text-green-800 dark:text-green-200" : "text-red-800 dark:text-red-200"}`}>
                        {emailConfig.last_test_status === "success" ? "Connexion réussie" : "Échec de connexion"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Dernier test : {new Date(emailConfig.last_test_at).toLocaleString('fr-FR')}
                      </p>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Serveur SMTP</Label>
                    <Input
                      placeholder="smtp.gmail.com"
                      value={emailConfig.smtp_host}
                      onChange={(e) => setEmailConfig({...emailConfig, smtp_host: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Port</Label>
                    <Input
                      type="number"
                      placeholder="465"
                      value={emailConfig.smtp_port}
                      onChange={(e) => setEmailConfig({...emailConfig, smtp_port: parseInt(e.target.value) || 465})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Utilisateur SMTP</Label>
                    <Input
                      placeholder="votre-email@gmail.com"
                      value={emailConfig.smtp_user}
                      onChange={(e) => setEmailConfig({...emailConfig, smtp_user: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Mot de passe</Label>
                    <Input
                      type="password"
                      placeholder="Mot de passe ou clé d'application"
                      value={emailConfig.smtp_password}
                      onChange={(e) => setEmailConfig({...emailConfig, smtp_password: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email expéditeur</Label>
                    <Input
                      placeholder="noreply@monentreprise.com"
                      value={emailConfig.from_email}
                      onChange={(e) => setEmailConfig({...emailConfig, from_email: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nom expéditeur</Label>
                    <Input
                      placeholder="Mon Entreprise"
                      value={emailConfig.from_name}
                      onChange={(e) => setEmailConfig({...emailConfig, from_name: e.target.value})}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    checked={emailConfig.use_ssl}
                    onCheckedChange={(checked) => setEmailConfig({...emailConfig, use_ssl: checked})}
                  />
                  <Label>Utiliser SSL (port 465). Désactivez pour TLS (port 587)</Label>
                </div>

                <div className="flex flex-wrap gap-3 pt-4 border-t">
                  <Button onClick={handleSaveConfig} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Enregistrer
                  </Button>
                  <Button variant="outline" onClick={handleTestConnection} disabled={testing || !emailConfig.smtp_host}>
                    {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    Tester la connexion
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Test email */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Send className="w-4 h-4" />
                  Envoyer un email de test
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col gap-3">
                  <Input
                    placeholder="votre-email@test.com"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    className="max-w-sm"
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleSendTestEmail("participation")}
                      disabled={sendingTest || !emailConfig.smtp_enabled}
                    >
                      {sendingTest ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <User className="w-4 h-4 mr-1" />}
                      Participation
                    </Button>
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => handleSendTestEmail("winner")}
                      disabled={sendingTest || !emailConfig.smtp_enabled}
                    >
                      {sendingTest ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trophy className="w-4 h-4 mr-1" />}
                      Gagnant
                    </Button>
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => handleSendTestEmail("reminder")}
                      disabled={sendingTest || !emailConfig.smtp_enabled}
                    >
                      {sendingTest ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Clock className="w-4 h-4 mr-1" />}
                      Relance
                    </Button>
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => handleSendTestEmail("results")}
                      disabled={sendingTest || !emailConfig.smtp_enabled}
                    >
                      {sendingTest ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Link2 className="w-4 h-4 mr-1" />}
                      Résultats
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Email Templates Preview */}
          <TabsContent value="templates" className="space-y-6">
            <div className="flex flex-wrap gap-2 mb-4">
              <Button
                variant={previewTemplate === "participation" ? "default" : "outline"}
                size="sm"
                onClick={() => setPreviewTemplate("participation")}
              >
                <User className="w-4 h-4 mr-2" />
                Participation
              </Button>
              <Button
                variant={previewTemplate === "winner" ? "default" : "outline"}
                size="sm"
                onClick={() => setPreviewTemplate("winner")}
              >
                <Trophy className="w-4 h-4 mr-2" />
                Vous avez gagné
              </Button>
              <Button
                variant={previewTemplate === "reminder" ? "default" : "outline"}
                size="sm"
                onClick={() => setPreviewTemplate("reminder")}
              >
                <Clock className="w-4 h-4 mr-2" />
                Relance gagnant
              </Button>
              <Button
                variant={previewTemplate === "results" ? "default" : "outline"}
                size="sm"
                onClick={() => setPreviewTemplate("results")}
              >
                <Link2 className="w-4 h-4 mr-2" />
                Lien résultats
              </Button>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Aperçu de l'email
                </CardTitle>
                <CardDescription>
                  Voici à quoi ressemblera l'email envoyé aux participants
                </CardDescription>
              </CardHeader>
              <CardContent className="bg-slate-100 dark:bg-slate-800 p-6 rounded-lg">
                {renderEmailPreview()}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Organizer Info */}
          <TabsContent value="org" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Informations de l'organisateur
                </CardTitle>
                <CardDescription>
                  Ces informations apparaîtront dans les emails envoyés aux participants
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Nom de l'organisation
                    </Label>
                    <Input
                      placeholder="Mon Entreprise"
                      value={orgInfo.name}
                      onChange={(e) => setOrgInfo({...orgInfo, name: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Email de contact
                    </Label>
                    <Input
                      placeholder="contact@monentreprise.com"
                      value={orgInfo.email}
                      onChange={(e) => setOrgInfo({...orgInfo, email: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      Téléphone
                    </Label>
                    <Input
                      placeholder="01 23 45 67 89"
                      value={orgInfo.phone}
                      onChange={(e) => setOrgInfo({...orgInfo, phone: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Site web
                    </Label>
                    <Input
                      placeholder="https://www.monentreprise.com"
                      value={orgInfo.website}
                      onChange={(e) => setOrgInfo({...orgInfo, website: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Adresse
                    </Label>
                    <Textarea
                      placeholder="123 Rue de Paris, 75001 Paris"
                      value={orgInfo.address}
                      onChange={(e) => setOrgInfo({...orgInfo, address: e.target.value})}
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Logo (URL)</Label>
                    <Input
                      placeholder="https://example.com/logo.png"
                      value={orgInfo.logo}
                      onChange={(e) => setOrgInfo({...orgInfo, logo: e.target.value})}
                    />
                    {orgInfo.logo && (
                      <div className="mt-2 p-4 bg-muted rounded-lg inline-block">
                        <img src={orgInfo.logo} alt="Logo preview" className="h-12 object-contain" />
                      </div>
                    )}
                  </div>
                </div>

                <Button onClick={handleSaveConfig} disabled={saving} className="mt-4">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Enregistrer les informations
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
